
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api from '../../../services/api';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  Close as CloseIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const getToken = () => localStorage.getItem('token');

const getRoleFromToken = () => {
  try {
    const token = getToken();
    if (!token) return '';

    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );

    return String(payload.role || '').trim().toLowerCase();
  } catch {
    return '';
  }
};

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${getToken()}`,
  },
});

const getCurrentLocalDateTime = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
};

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(String(value).slice(0, 10) + 'T00:00:00');

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';

  const date = new Date(String(value).replace(' ', 'T'));

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const getGradeColor = (grade) => {
  if (grade === 'HEAVY') return 'error';
  if (grade === 'MEDIUM') return 'warning';
  if (grade === 'LIGHT') return 'success';
  return 'default';
};

const ShellActivities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const role = getRoleFromToken();
  const isAdmin = role === 'admin';
  const isShellUser = role === 'shell';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const [fitConfirmOpen, setFitConfirmOpen] = useState(false);
  const [fitSaving, setFitSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCoach, setHistoryCoach] = useState('');

  const [form, setForm] = useState({
    shell_gang_no: '',
    shell_grading: '',
    remark: '',
    change_reason: '',
    shell_repairing_time: '',
  });

  const fetchShellList = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/shell/activities', {
        ...authConfig(),
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search.trim(),
        },
      });

      const result = response.data;
      setRows(result.data || []);
      setTotal(Number(result.pagination?.total || 0));
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load shell coach list'
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchShellList();
  }, [fetchShellList]);

  const openEdit = (row) => {
    setSelected(row);
    setForm({
      shell_gang_no: row.shell_gang_no || '',
      shell_grading: row.shell_grading || '',
      shell_repairing_time: row.shell_repairing_time ?? '',
      remark: row.remark || '',
      change_reason: '',
    });
    setError('');
    setSuccess('');
    setFormOpen(true);
  };

  const closeEdit = () => {
    if (saving || fitSaving) return;
    setFormOpen(false);
    setSelected(null);
    setFitConfirmOpen(false);
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const gradeOrder = ['LIGHT', 'MEDIUM', 'HEAVY'];
  const oldGrade = selected?.shell_grading || '';

  const allowedGrades = useMemo(() => {
    if (isAdmin) return gradeOrder;

    if (isShellUser) {
      if (!oldGrade) return [];

      return gradeOrder.filter(
        (grade) =>
          gradeOrder.indexOf(grade) >= gradeOrder.indexOf(oldGrade)
      );
    }

    return [];
  }, [isAdmin, isShellUser, oldGrade]);

  const handleSave = async () => {
    if (!selected) return;

    if (
      form.shell_grading &&
      !allowedGrades.includes(form.shell_grading)
    ) {
      setError('You are not permitted to select this grading.');
      return;
    }

    if (
      isAdmin &&
      oldGrade &&
      form.shell_grading &&
      form.shell_grading !== oldGrade &&
      !form.change_reason.trim()
    ) {
      setError('Please provide a reason for the grading change.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    

    try {
      await api.post(
        `/shell/activities/${selected.visit_id}`,
        {
          // The fit date is intentionally not changed by ordinary Save.
          shell_fit_date: selected.shell_fit_date || null,
          shell_gang_no: form.shell_gang_no.trim().toUpperCase() || null,
          shell_grading: form.shell_grading || null,
          shell_repairing_time: form.shell_repairing_time || null,
          remark: form.remark.trim(),
          change_reason: form.change_reason.trim(),
        },
        authConfig()
      );

      setFormOpen(false);
      setSelected(null);
      setSuccess('Shell details saved successfully.');
      await fetchShellList();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to save shell details'
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmShellFit = async () => {
    if (!selected) return;

    setFitSaving(true);
    setError('');
    setSuccess('');

    const fitDateTime = getCurrentLocalDateTime();

    try {
      await api.post(
        `/shell/activities/${selected.visit_id}`,
        {
          shell_fit_date: fitDateTime,
          shell_gang_no: form.shell_gang_no.trim().toUpperCase() || null,
          shell_grading: form.shell_grading || null,
          shell_repairing_time: form.shell_repairing_time || null,
          remark: form.remark.trim(),
          change_reason: form.change_reason.trim(),
        },
        authConfig()
      );

      setSelected((previous) => ({
        ...previous,
        shell_fit_date: fitDateTime,
      }));

      setFitConfirmOpen(false);
      setSuccess('Shell fit date and time recorded successfully.');

      await fetchShellList();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to record shell fit date and time'
      );
    } finally {
      setFitSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryCoach(row.coach_no);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryRows([]);
    setError('');

    try {
      const response = await api.get(
        `/shell/activities/${row.visit_id}/history`,
        authConfig()
      );

      setHistoryRows(response.data.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load grading history'
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setHistoryRows([]);
  };

  const getLiftDisplay = (row) => {
    if (row.lift_status === 'LIFT_UP') {
      return {
        status: 'LIFT UP',
        detail: row.lift_place || '—',
        color: 'success',
      };
    }

    if (row.lift_status === 'LIFT_DOWN') {
      return {
        status: 'LIFT DOWN',
        detail: [row.trolley_no_1, row.trolley_no_2]
          .filter(Boolean)
          .join(' / ') || '—',
        color: 'warning',
      };
    }

    if (row.lift_status === 'FALSE_LIFT') {
      return {
        status: 'FALSE LIFT',
        detail: [row.trolley_no_1, row.trolley_no_2]
          .filter(Boolean)
          .join(' / ') || '—',
        color: 'default',
      };
    }

    return {
      status: 'NOT RECORDED',
      detail: '—',
      color: 'default',
    };
  };

  const renderGrade = (grade) => {
    if (!grade) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not assigned
        </Typography>
      );
    }

    return (
      <Chip
        size="small"
        label={grade}
        color={getGradeColor(grade)}
        variant="outlined"
      />
    );
  };

  const renderMobileCoachCard = (row) => {
    const lift = getLiftDisplay(row);

    return (
      <Paper
        key={row.visit_id}
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {row.coach_no}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Visit: {row.visit_no || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Type: {row.coach_type_code || '—'}
            </Typography>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            {renderGrade(row.shell_grading)}
          </Box>
        </Stack>

        <Divider sx={{ my: 1.25 }} />

        <Grid container spacing={1.25}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Lift Status
            </Typography>
            <Box sx={{ mt: 0.25 }}>
              <Chip
                size="small"
                label={lift.status}
                color={lift.color}
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Lift Place / Trolley
            </Typography>
            <Typography
              variant="body2"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {lift.detail}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Shell Fit Date
            </Typography>
            <Typography variant="body2">
              {formatDateTime(row.shell_fit_date)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Gang No.
            </Typography>
            <Typography variant="body2">
              {row.shell_gang_no || '—'}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.25 }} />

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          flexWrap="wrap"
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => openHistory(row)}
          >
            History
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => openEdit(row)}
            disabled={!isAdmin && !isShellUser}
          >
            Edit
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {/* PAGE HEADER */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Shell Activities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coaches currently inside the shell section
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchShellList}
          disabled={loading}
          fullWidth={isMobile}
        >
          Refresh
        </Button>
      </Stack>

      {/* ALERTS */}
      {error && !formOpen && !historyOpen && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* SEARCH */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          value={search}
          placeholder="Search coach number, visit number, or gang number"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
          }}
        />
      </Paper>

      {/* MOBILE COACH CARDS */}
      {isMobile && (
        <Stack spacing={1.5}>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Paper>
          ) : rows.length === 0 ? (
            <Alert severity="info">
              No coaches currently in the shell section.
            </Alert>
          ) : (
            rows.map(renderMobileCoachCard)
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'center',
                px: 0,
              },
            }}
          />
        </Stack>
      )}

      {/* DESKTOP TABLE */}
      {!isMobile && (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', overflowX: 'auto' }}
        >
          <Table size="small" sx={{ minWidth: 1050 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {[
                  'Coach No.',
                  'Coach Type',
                  'Lift Status',
                  'Lift Place / Trolley',
                  'Shell Fit Date',
                  'Gang No.',
                  'Grading',
                  'Actions',
                ].map((heading) => (
                  <TableCell key={heading} sx={{ fontWeight: 700 }}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    No coaches currently in the shell section.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const lift = getLiftDisplay(row);

                  return (
                    <TableRow hover key={row.visit_id}>
                      <TableCell>
                        <Typography fontWeight={600}>
                          {row.coach_no}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {row.visit_no}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {row.coach_type_code || '—'}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={lift.status}
                          color={lift.color}
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>{lift.detail}</TableCell>

                      <TableCell>
                        {formatDateTime(row.shell_fit_date)}
                      </TableCell>

                      <TableCell>{row.shell_gang_no || '—'}</TableCell>

                      <TableCell>
                        {renderGrade(row.shell_grading)}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit shell details">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(row)}
                                disabled={!isAdmin && !isShellUser}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Grading history">
                            <IconButton
                              size="small"
                              onClick={() => openHistory(row)}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableContainer>
      )}

      {/* SHELL DETAILS DIALOG */}
      <Dialog
        open={formOpen}
        onClose={closeEdit}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                Shell Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coach: {selected?.coach_no || '—'}
              </Typography>
            </Box>

            <IconButton onClick={closeEdit} disabled={saving || fitSaving}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* SHELL FIT DATE/TIME ACTION */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  Shell Fit Date & Time
                </Typography>

                <Typography fontWeight={700}>
                  {formatDateTime(selected?.shell_fit_date)}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="success"
                fullWidth={isMobile}
                startIcon={<CalendarIcon />}
                onClick={() => setFitConfirmOpen(true)}
                disabled={saving || fitSaving}
              >
                {selected?.shell_fit_date
                  ? 'Update Fit Date'
                  : 'Mark Shell Fit'}
              </Button>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {/* GANG NUMBER */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Shell Gang No."
                value={form.shell_gang_no}
                onChange={handleFormChange('shell_gang_no')}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                }}
              />
            </Grid>

            {/* GRADING */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Shell Grading</InputLabel>
                <Select
                  label="Shell Grading"
                  value={form.shell_grading}
                  onChange={handleFormChange('shell_grading')}
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {allowedGrades.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isShellUser && !oldGrade && (
                <Typography variant="caption" color="text.secondary">
                  An admin must assign the initial grading.
                </Typography>
              )}
            </Grid>


            <Grid item xs={12} sm={6}>
  <TextField
    fullWidth
    size="small"
    type="number"
    label="Shell Repairing Time (Hours)"
    value={form.shell_repairing_time}
    onChange={handleFormChange('shell_repairing_time')}
    inputProps={{ min: 0, step: 1 }}
  />
</Grid>

            {/* GRADING CHANGE REASON */}
            {form.shell_grading &&
              oldGrade &&
              form.shell_grading !== oldGrade && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason for grading change"
                    value={form.change_reason}
                    onChange={handleFormChange('change_reason')}
                    required={isAdmin}
                    multiline
                    minRows={2}
                  />
                </Grid>
              )}

            {/* REMARK */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Remarks"
                value={form.remark}
                onChange={handleFormChange('remark')}
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: 'stretch',
            '& > :not(style) ~ :not(style)': {
              ml: { xs: 0, sm: 1 },
              mt: { xs: 1, sm: 0 },
            },
          }}
        >
          <Button
            onClick={closeEdit}
            disabled={saving || fitSaving}
            fullWidth={isMobile}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || fitSaving || (!isAdmin && !isShellUser)}
            fullWidth={isMobile}
          >
            {saving ? 'Saving...' : 'Save Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SHELL FIT CONFIRMATION */}
      <Dialog
        open={fitConfirmOpen}
        onClose={() => !fitSaving && setFitConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Shell Fit</DialogTitle>

        <DialogContent>
          <Typography>
            Record the current date and time as the shell fit date for coach
            <strong> {selected?.coach_no || '—'}</strong>?
          </Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            The fit date and time will be saved when you confirm.
          </Alert>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: 'stretch',
            '& > :not(style) ~ :not(style)': {
              ml: { xs: 0, sm: 1 },
              mt: { xs: 1, sm: 0 },
            },
          }}
        >
          <Button
            onClick={() => setFitConfirmOpen(false)}
            disabled={fitSaving}
            fullWidth={isMobile}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={confirmShellFit}
            disabled={fitSaving}
            fullWidth={isMobile}
          >
            {fitSaving ? 'Recording...' : 'Confirm & Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GRADING HISTORY DIALOG */}
      <Dialog
        open={historyOpen}
        onClose={closeHistory}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                Grading History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coach: {historyCoach}
              </Typography>
            </Box>

            <IconButton onClick={closeHistory}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {historyLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : historyRows.length === 0 ? (
            <Alert severity="info">
              No grading changes have been recorded.
            </Alert>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {historyRows.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {formatDateTime(item.changed_at)}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mt: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      size="small"
                      label={item.old_grading || 'Initial'}
                      variant="outlined"
                    />

                    <Typography color="text.secondary">
                      →
                    </Typography>

                    <Chip
                      size="small"
                      label={item.new_grading}
                      color={getGradeColor(item.new_grading)}
                    />
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2">
                    <strong>Changed by:</strong> {item.changed_by || '—'}
                  </Typography>

                  <Typography variant="body2">
                    <strong>Role:</strong> {item.user_role || '—'}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <strong>Reason:</strong> {item.change_reason || '—'}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    {[
                      'Changed At',
                      'Old Grading',
                      'New Grading',
                      'Changed By',
                      'Role',
                      'Reason',
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{ fontWeight: 700 }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {historyRows.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ minWidth: 150 }}>
                        {formatDateTime(item.changed_at)}
                      </TableCell>

                      <TableCell>
                        {item.old_grading || 'Initial'}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={item.new_grading}
                          color={getGradeColor(item.new_grading)}
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        {item.changed_by || '—'}
                      </TableCell>

                      <TableCell>
                        {item.user_role || '—'}
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: 'normal',
                          minWidth: 160,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {item.change_reason || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeHistory} fullWidth={isMobile}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShellActivities;

